import { useNetwork, type ApiError } from './network';
import { useUser } from '../contexts/UserContext';
import {
  generateIdempotencyKey,
  storeIdempotencyKey,
  getSecurityHeaders,
  validateSecurityResponse,
  handleSecurityError,
  retryWithBackoff,
  IdempotencyKeyInfo
} from './securityUtils';

/**
 * Secure network utility that wraps the existing network utility with security features
 */
export const useSecureNetwork = () => {
  const { get, post, put, del } = useNetwork();
  const { user } = useUser();

  /**
   * Secure POST request with idempotency key and security features
   */
  const securePost = async (
    endpoint: string,
    body: any,
    operation: string,
    auth: boolean = true,
    options: {
      useIdempotency?: boolean;
      customIdempotencyKey?: string;
      retryAttempts?: number;
    } = {}
  ) => {
    const {
      useIdempotency = true,
      customIdempotencyKey,
      retryAttempts = 3
    } = options;

    return retryWithBackoff(async () => {
      let idempotencyKey = customIdempotencyKey;

      if (useIdempotency && !idempotencyKey) {
        idempotencyKey = generateIdempotencyKey(
          operation,
          user?.id?.toString()
        );

        // Store the key for tracking
        const keyInfo: IdempotencyKeyInfo = {
          key: idempotencyKey,
          timestamp: Date.now(),
          operation,
          userId: user?.id?.toString()
        };
        storeIdempotencyKey(keyInfo);
      }

      // Prepare headers
      const securityHeaders =
        useIdempotency && idempotencyKey
          ? getSecurityHeaders(idempotencyKey)
          : {};

      try {
        // Make the request with security headers
        const response = await post(endpoint, body, auth, securityHeaders);

        // Validate security response
        validateSecurityResponse(response);

        return response;
      } catch (error: any) {
        // Handle security-specific errors. The HTTP status and body stay on the
        // rethrown error: a caller telling "the server refused" from "the
        // connection dropped, it may have gone through" needs more than the text.
        // (No `response` key, so retryWithBackoff's short-circuits are unchanged.)
        const securityError = new Error(
          handleSecurityError(error, operation)
        ) as ApiError;
        securityError.status = error?.status ?? error?.response?.status;
        securityError.data = error?.data ?? error?.response?.data;
        throw securityError;
      }
    }, retryAttempts);
  };

  /**
   * Secure GET request
   */
  const secureGet = async (
    endpoint: string,
    auth: boolean = true,
    options: {
      retryAttempts?: number;
    } = {}
  ) => {
    const { retryAttempts = 2 } = options;

    return retryWithBackoff(async () => {
      try {
        const response = await get(endpoint, auth);
        validateSecurityResponse(response);
        return response;
      } catch (error: any) {
        const securityError = handleSecurityError(error, 'GET request');
        throw new Error(securityError);
      }
    }, retryAttempts);
  };

  /**
   * Secure PUT request
   */
  const securePut = async (
    endpoint: string,
    body: any,
    operation: string,
    auth: boolean = true,
    options: {
      useIdempotency?: boolean;
      retryAttempts?: number;
    } = {}
  ) => {
    const { useIdempotency = false, retryAttempts = 2 } = options;

    return retryWithBackoff(async () => {
      let headers = {};

      if (useIdempotency) {
        const idempotencyKey = generateIdempotencyKey(
          operation,
          user?.id?.toString()
        );
        headers = getSecurityHeaders(idempotencyKey);
      }

      try {
        const response = await put(endpoint, body, auth, headers);
        validateSecurityResponse(response);
        return response;
      } catch (error: any) {
        const securityError = handleSecurityError(error, operation);
        throw new Error(securityError);
      }
    }, retryAttempts);
  };

  /**
   * Token withdrawal with enhanced security
   */
  const secureTokenWithdraw = async (
    amount: string,
    twoFactorToken?: string
  ) => {
    return securePost(
      '/secure-tokens/withdraw',
      { amount, twoFactorToken },
      'TOKEN_WITHDRAWAL',
      true,
      { useIdempotency: true, retryAttempts: 1 } // No retries for withdrawals
    );
  };

  /**
   * Token withdrawal confirmation with enhanced security
   */
  const secureTokenWithdrawConfirm = async (
    serializedTransaction: string,
    idempotencyKey: string
  ) => {
    return securePost(
      '/secure-tokens/confirm-withdraw',
      { serializedTransaction, idempotencyKey },
      'TOKEN_WITHDRAWAL_CONFIRM',
      true,
      {
        useIdempotency: true,
        customIdempotencyKey: idempotencyKey,
        retryAttempts: 1
      }
    );
  };

  /**
   * Token deposit with enhanced security
   */
  const secureTokenDeposit = async (
    amount: string,
    twoFactorToken?: string
  ) => {
    return securePost(
      '/secure-tokens/deposit',
      { amount, twoFactorToken },
      'TOKEN_DEPOSIT',
      true,
      { useIdempotency: true, retryAttempts: 1 }
    );
  };

  /**
   * Token deposit confirmation with enhanced security
   */
  const secureTokenDepositConfirm = async (
    serializedTransaction: string,
    idempotencyKey: string
  ) => {
    return securePost(
      '/secure-tokens/confirm-deposit',
      { serializedTransaction, idempotencyKey },
      'TOKEN_DEPOSIT_CONFIRM',
      true,
      {
        useIdempotency: true,
        customIdempotencyKey: idempotencyKey,
        retryAttempts: 1
      }
    );
  };

  /**
   * NFT withdrawal with enhanced security
   */
  const secureNftWithdraw = async (
    nftId: number,
    amount: number = 1,
    twoFactorToken?: string
  ) => {
    return securePost(
      '/secure-nfts/withdraw',
      { nftId, amount, twoFactorToken },
      'NFT_WITHDRAWAL',
      true,
      { useIdempotency: true, retryAttempts: 1 }
    );
  };

  /**
   * NFT withdrawal confirmation with enhanced security
   */
  const secureNftWithdrawConfirm = async (
    serializedTransaction: string,
    idempotencyKey: string
  ) => {
    return securePost(
      '/secure-nfts/confirm-withdraw',
      { serializedTransaction, idempotencyKey },
      'NFT_WITHDRAWAL_CONFIRM',
      true,
      {
        useIdempotency: true,
        customIdempotencyKey: idempotencyKey,
        retryAttempts: 1
      }
    );
  };

  const secureNftWithdrawCancel = async (idempotencyKey: string) => {
    return securePost(
      '/secure-nfts/cancel-withdraw',
      { idempotencyKey },
      'NFT_WITHDRAWAL_CANCEL',
      true,
      {
        useIdempotency: true,
        customIdempotencyKey: idempotencyKey,
        retryAttempts: 1
      }
    );
  };

  const secureNftDeposit = async (
    nftId: number,
    amount: number = 1,
    twoFactorToken?: string
  ) => {
    return securePost(
      '/secure-nfts/deposit',
      { nftId, amount, twoFactorToken },
      'NFT_DEPOSIT',
      true,
      { useIdempotency: true, retryAttempts: 1 }
    );
  };

  const secureNftDepositConfirm = async (
    serializedTransaction: string,
    idempotencyKey: string
  ) => {
    return securePost(
      '/secure-nfts/confirm-deposit',
      { serializedTransaction, idempotencyKey },
      'NFT_DEPOSIT_CONFIRM',
      true,
      {
        useIdempotency: true,
        customIdempotencyKey: idempotencyKey,
        retryAttempts: 1
      }
    );
  };

  /**
   * Get token price (read-only, no idempotency needed)
   */
  const getTokenPrice = async () => {
    return secureGet('/secure-tokens/price', true, { retryAttempts: 2 });
  };

  /**
   * Get historical token price (read-only, no idempotency needed)
   */
  const getHistoricalTokenPrice = async (timeframe: string = '24h') => {
    return secureGet(
      `/secure-tokens/historical-price?timeframe=${timeframe}`,
      true,
      { retryAttempts: 2 }
    );
  };

  /**
   * Get unique NFTs (read-only, no idempotency needed)
   */
  const getUniqueNfts = async () => {
    return secureGet('/secure-nfts/unique', true, { retryAttempts: 2 });
  };

  /**
   * Health check for secure services
   */
  const checkSecureServicesHealth = async () => {
    try {
      const [tokenHealth, nftHealth] = await Promise.all([
        secureGet('/secure-tokens/health', false),
        secureGet('/secure-nfts/health', false)
      ]);

      return {
        tokenService: tokenHealth,
        nftService: nftHealth,
        overall:
          tokenHealth.status === 'healthy' && nftHealth.status === 'healthy'
      };
    } catch (error) {
      console.error('Secure services health check failed:', error);
      return {
        tokenService: { status: 'unhealthy' },
        nftService: { status: 'unhealthy' },
        overall: false
      };
    }
  };

  return {
    // Generic secure methods
    securePost,
    secureGet,
    securePut,

    // Token operations
    secureTokenWithdraw,
    secureTokenWithdrawConfirm,
    secureTokenDeposit,
    secureTokenDepositConfirm,

    // NFT operations
    secureNftWithdraw,
    secureNftWithdrawConfirm,
    secureNftWithdrawCancel,
    secureNftDeposit,
    secureNftDepositConfirm,

    // Read operations
    getTokenPrice,
    getHistoricalTokenPrice,
    getUniqueNfts,

    // Health checks
    checkSecureServicesHealth,

    // Legacy methods for backward compatibility (with security wrapper)
    post: securePost,
    get: secureGet,
    put: securePut
  };
};
